// Novedades del mes: horas extras, faltas, permisos, subsidios.
// Nómina → Movimientos. Validaciones legales (Art. 55 CT) en la RPC.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { Campo, Modal, fieldCls, fecha, nomRpc, TIPOS_NOVEDAD, useDialogoEnLinea, type EmpleadoRow, type NovedadRow, hoyISO } from './shared';
import { ExportarBotones, type Celda } from '@/lib/utils/exportarTabla';
import { fechaLocal } from '@/lib/utils/fecha';

const hoy = hoyISO();
const mesIni = hoy.slice(0, 8) + '01';
const vacio = { empleado_id: '', tipo: 'hora_suplementaria', fecha: hoy, cantidad: '', motivo: '' };

export function Novedades() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const [desde, setDesde] = useState(mesIni);
  const [hasta, setHasta] = useState(hoy);
  const [tipo, setTipo] = useState('');
  const { data: rows = [], isFetching } = useQuery<NovedadRow[]>({
    queryKey: ['nom-novedades', desde, hasta, tipo],
    queryFn: () => nomRpc<NovedadRow[]>('nom_novedades', { _empleado_id: null, _desde: desde || null, _hasta: hasta || null, _tipo: tipo || null }),
  });
  const { data: empleados = [] } = useQuery<EmpleadoRow[]>({
    queryKey: ['nom-empleados-opt-act'],
    queryFn: () => nomRpc<EmpleadoRow[]>('nom_empleados', { _q: null, _solo_activos: true }),
  });

  const [abierto, setAbierto] = useState(false);
  const [form, setForm] = useState<typeof vacio>(vacio);
  const unidad = TIPOS_NOVEDAD[form.tipo]?.unidad ?? 'horas';

  const guardar = useMutation({
    mutationFn: () => nomRpc('nom_novedad_guardar', {
      _data: {
        empleado_id: form.empleado_id, tipo: form.tipo, fecha: form.fecha,
        motivo: form.motivo || null,
        ...(unidad === 'horas' ? { horas: Number(form.cantidad) } : { dias: Number(form.cantidad) }),
      },
    }),
    onSuccess: () => { toast.success('Novedad registrada'); setAbierto(false); void qc.invalidateQueries({ queryKey: ['nom-novedades'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => nomRpc('nom_novedad_eliminar', { _id: id }),
    onSuccess: () => { toast.success('Novedad eliminada'); void qc.invalidateQueries({ queryKey: ['nom-novedades'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const cantidadDe = (n: NovedadRow) => (n.horas != null ? Number(n.horas) : Number(n.dias));
  const totalCantidad = rows.reduce((s, n) => s + cantidadDe(n), 0);
  const headersExp = ['N°', 'Fecha', 'Empleado', 'Tipo', 'Cantidad', 'Motivo', 'Rol'];
  const filasExport: Celda[][] = [
    ...rows.map((n, i): Celda[] => [
      i + 1, fecha(n.fecha), n.razon_social, (TIPOS_NOVEDAD[n.tipo]?.label ?? n.tipo).split(' (')[0],
      cantidadDe(n), n.motivo ?? '', n.rol_id ? 'Liquidada' : 'Pendiente',
    ]),
    ['', '', '', 'TOTAL', totalCantidad, '', ''],
  ];

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Novedades</h1>
        <div className="flex items-center gap-2">
          <ExportarBotones
            nombre={`novedades-${fechaLocal()}`} titulo="Novedades"
            headers={headersExp} filas={filasExport} colsDerecha={[0, 4]} disabled={rows.length === 0}
          />
          <Button onClick={() => { setForm(vacio); setAbierto(true); }}><Plus className="w-4 h-4 mr-1" /> Registrar novedad</Button>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <Campo label="Desde"><input type="date" className={fieldCls} value={desde} onChange={(e) => setDesde(e.target.value)} /></Campo>
        <Campo label="Hasta"><input type="date" className={fieldCls} value={hasta} onChange={(e) => setHasta(e.target.value)} /></Campo>
        <Campo label="Tipo">
          <select className={`${fieldCls} w-64`} value={tipo} onChange={(e) => setTipo(e.target.value)}>
            <option value="">Todos</option>
            {Object.entries(TIPOS_NOVEDAD).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </Campo>
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Novedades ({rows.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Fecha</th><th className="py-2 pr-3">Empleado</th><th className="py-2 pr-3">Tipo</th>
              <th className="py-2 pr-3 text-right">Cantidad</th><th className="py-2 pr-3">Motivo</th><th className="py-2 pr-3">Rol</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && !isFetching && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">Sin novedades en el rango</td></tr>}
              {rows.map((n) => (
                <tr key={n.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 font-mono">{fecha(n.fecha)}</td>
                  <td className="py-2 pr-3">{n.razon_social}<div className="text-xs text-muted-foreground font-mono">{n.identificacion}</div></td>
                  <td className="py-2 pr-3">{(TIPOS_NOVEDAD[n.tipo]?.label ?? n.tipo).split(' (')[0]}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{n.horas != null ? `${Number(n.horas)} h` : `${Number(n.dias)} d`}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{n.motivo}</td>
                  <td className="py-2 pr-3">{n.rol_id ? <span className="text-xs px-2 py-0.5 rounded-full bg-completado-soft text-completado">Liquidada</span> : <span className="text-xs text-muted-foreground">Pendiente</span>}</td>
                  <td className="py-2 text-right">
                    {!n.rol_id && (
                      <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-destructive hover:bg-destructive/10" title="Eliminar" onClick={async () => { if (await dlg.confirmar('¿Eliminar novedad?', { peligro: true, aceptar: 'Eliminar' })) eliminar.mutate(n.id); }}><Trash2 className="w-4 h-4" /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal title="Registrar novedad" onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <Campo label="Empleado *">
              <select className={fieldCls} value={form.empleado_id} onChange={(e) => setForm({ ...form, empleado_id: e.target.value })}>
                <option value="">— Selecciona —</option>
                {empleados.map((e) => <option key={e.id} value={e.id}>{e.razon_social} ({e.identificacion})</option>)}
              </select>
            </Campo>
            <Campo label="Tipo *">
              <select className={fieldCls} value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value, cantidad: '' })}>
                {Object.entries(TIPOS_NOVEDAD).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </Campo>
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Fecha *"><input type="date" className={fieldCls} value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} /></Campo>
              <Campo label={unidad === 'horas' ? 'Horas *' : 'Días *'}>
                <input type="number" min="0.5" step="0.5" className={fieldCls} value={form.cantidad} onChange={(e) => setForm({ ...form, cantidad: e.target.value })} />
              </Campo>
            </div>
            {form.tipo === 'hora_suplementaria' && (
              <div className="text-xs text-muted-foreground">Art. 55 CT: máximo 4 horas al día y 12 a la semana (recargo 50%).</div>
            )}
            <Campo label="Motivo"><input className={fieldCls} value={form.motivo} onChange={(e) => setForm({ ...form, motivo: e.target.value })} /></Campo>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
              <Button isLoading={guardar.isPending} disabled={!form.empleado_id || !form.fecha || !Number(form.cantidad)} onClick={() => guardar.mutate()}>Registrar</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
